import type { CollectorContributionContext } from '@/lib/community-collector.storage';
import type { CollectionPlace } from '@/features/collection-place/collection-place.contract';
import type { WordItem } from './types';
import type { NavigateTranslationPayload } from './components/SpeakPhraseTranslationActions';

/**
 * Non-visual rules of the "Enregistrer" session, kept pure so tests can lock
 * them: batch paging, resume position, upload payload, local storage merges
 * and the round trip to transcription-phrase.
 */

/** Phrases per batch requested from wordApi.getFilesByLang. */
export const SPEAK_PAGE_SIZE = 5;

/** 1-based position of a phrase across batches, as stored by progressApi. */
export const globalPhraseIndex = (page: number, limit: number, index: number) => (page - 1) * limit + index + 1;

/** Where to resume from the backend's last_index, or null when unusable. */
export function resumeFromLastIndex(lastIndex: unknown, limit: number): { page: number; sentence: number } | null {
  const lastDone = Number(lastIndex);
  if (isNaN(lastDone) || lastDone < 0) return null;
  return { page: Math.max(1, Math.floor(lastDone / limit) + 1), sentence: lastDone % limit };
}

/** Where to resume from the locally saved `{ page, currentSentence }`. */
export function parseSavedProgress(raw: string): { page: number; sentence: number } {
  const saved = JSON.parse(raw || '{}');
  return {
    page: Math.max(1, Number(saved.page) || 1),
    sentence: Math.max(0, Number(saved.currentSentence) || 0),
  };
}

export const speakStorageKeys = (userId: string | number | undefined, language: string) => {
  const owner = userId ?? 'anon';
  const lang = language || 'none';
  return {
    progress: `speak_progress_${owner}_${lang}`,
    completed: `speak_completed_${owner}_${lang}`,
    translations: (page: number) => `speak_translations_${owner}_${lang}_page_${page}`,
  };
};

/** The completed flags saved for `page`, or all false when absent or stale. */
export function completedFor(raw: string | null, page: number, count: number): boolean[] {
  if (raw) {
    const saved: Record<number, boolean[]> = JSON.parse(raw);
    if (saved[page] && saved[page].length === count) return saved[page];
  }
  return new Array(count).fill(false);
}

export type SavedTranslations = Record<string, { fr?: string; local?: string }>;

/** Applies the translations saved locally for a batch onto its items. */
export function mergeSavedTranslations(items: WordItem[], saved: SavedTranslations): WordItem[] {
  return items.map((item, index) => {
    const savedData = saved[String(index)];
    if (!savedData) return item;
    const translations = { ...(item.translations || {}) };
    if (savedData.fr) translations.fr = savedData.fr;
    return { ...item, text: savedData.local ? savedData.local : item.text, translations };
  });
}

/** What transcription-phrase hands back when the person returns to the session. */
export type ReturnedTranslation = { currentSentence: number; frenchTranslation?: string; localPhrase?: string };

/** Applies a returned translation to the batch: French always, local text only if the phrase had none. */
export function applyReturnedTranslation(items: WordItem[], state: ReturnedTranslation): WordItem[] {
  const next = [...items];
  const current = next[state.currentSentence];
  if (!current) return next;
  const translations = { ...current.translations };
  if (state.frenchTranslation) translations['fr'] = state.frenchTranslation;
  let text = current.text;
  if (state.localPhrase && (!text || text.trim() === '')) text = state.localPhrase;
  next[state.currentSentence] = { ...current, text, translations };
  return next;
}

/** Saves a returned translation in the batch's local translation map. */
export function rememberReturnedTranslation(map: SavedTranslations, state: ReturnedTranslation): SavedTranslations {
  if (!state.frenchTranslation && !state.localPhrase) return map;
  const key = String(state.currentSentence);
  const entry = { ...(map[key] ?? {}) };
  if (state.frenchTranslation) entry.fr = state.frenchTranslation;
  if (state.localPhrase) entry.local = state.localPhrase;
  return { ...map, [key]: entry };
}

export type SpeakUploadMeta = {
  lang: string;
  phraseLocal?: string;
  phraseFr?: string;
  recordingDurationMs: number;
  communityProfileId?: string | number;
  communityCode?: string;
  collectionPlace: CollectionPlace | null;
  phraseId?: string;
};

export type SpeakUploadCheck =
  | { ok: true; meta: SpeakUploadMeta; content: string }
  | { ok: false; reason: 'phraseMissing' | 'durationMissing' };

/**
 * The metadata sent with audioApi.uploadWordAudio for the phrase on screen,
 * or why it can't be sent. Checks run in the page's historical order: the
 * phrase first, then the recorded duration.
 */
export function buildSpeakUpload({
  item,
  language,
  recordingDurationMs,
  collector,
  place,
}: {
  item: WordItem;
  language: string;
  recordingDurationMs: number | null;
  collector: CollectorContributionContext | null;
  place: CollectionPlace | null;
}): SpeakUploadCheck {
  const phraseLocal = item.text || undefined;
  const translations = item.translations ?? {};
  const phraseFr = translations['fr'] || translations['fr_FR'] || Object.values(translations)[0] || undefined;
  if (!phraseLocal && !phraseFr) return { ok: false, reason: 'phraseMissing' };
  if (!recordingDurationMs || recordingDurationMs <= 0) return { ok: false, reason: 'durationMissing' };
  return {
    ok: true,
    meta: {
      lang: language,
      phraseLocal,
      phraseFr,
      recordingDurationMs,
      communityProfileId: collector?.communityProfileId,
      communityCode: collector?.communityProfileCode,
      collectionPlace: place,
      phraseId: item.id,
    },
    content: phraseLocal ?? phraseFr ?? `Audio ${language}`,
  };
}

/** After "next": stay in the batch, or fetch the next one. */
export const nextSpeakStep = (currentSentence: number, total: number): 'advance' | 'nextPage' =>
  currentSentence === total - 1 ? 'nextPage' : 'advance';

/** After "previous": step back in the batch, fetch the previous one, or nothing. */
export const previousSpeakStep = (currentSentence: number, page: number): 'back' | 'previousPage' | 'none' =>
  currentSentence > 0 ? 'back' : page > 1 ? 'previousPage' : 'none';

export type SpeakTranslationBase = {
  fromSpeakContribute: true;
  targetLanguage: string;
  returnTo: string;
  currentPage: number;
  currentSentence: number;
  isModerator: boolean;
  fromCollector?: boolean;
  collectorId?: string;
  communityProfileId?: string;
  communityProfileCode?: string;
  communityProfileLocation?: string;
};

/** Router state for the trip to transcription-phrase, by mode. */
export function translationNavigationState(payload: NavigateTranslationPayload, base: SpeakTranslationBase): Record<string, unknown> {
  if (payload.mode === 'toFrench') {
    return { ...base, localPhraseToTranslate: payload.localPhrase, needsTranslationToFrench: true };
  }
  if (payload.mode === 'toLocal') {
    return { ...base, frenchPhrase: payload.translation };
  }
  return { ...base, localPhraseToTranslate: payload.localPhrase, frenchPhrase: payload.translation, editMode: true };
}

/** French text a local-phrase edit is attached to: "fr" first, else the first non-empty translation. */
export function existingFrenchFor(item: WordItem | undefined): string | undefined {
  const tr = item?.translations || {};
  const preferred = (tr as Record<string, string>).fr;
  const firstNonEmpty = Object.values(tr || {}).find((value) => !!value && String(value).trim().length > 0);
  return (preferred && preferred.trim()) || (firstNonEmpty && String(firstNonEmpty).trim()) || undefined;
}
